import classNames from 'classnames';
import React, { useMemo } from 'react';
import { FormatNumberOptions, useIntl } from 'react-intl';
import { JobMessages } from 'src/intl/JobMessages';
import { assertNever } from 'src/utils/assert';
import { msToTime } from 'src/utils/logs/logHelpers';
import { TubeSheetChartType, TubeSheetGraphBreakpoint, TubeSheetGraphData } from './util/types';
import { DqText } from '../dq-text';
import { IonCol, IonRow } from '@ionic/react';
import { useBreakpoint } from 'src/utils/helpers/platformHelpers';

interface TubeSheetGraphKeyInnerProps {
  breakpoints?: TubeSheetGraphBreakpoint;
  showTimeDetails: boolean;
  isMetric: boolean;
}

interface TubeSheetGraphKeyItemProps {
  className?: string;
  title: string;
  subtitle?: string;
}

const TubeSheetGraphKeyItem: React.FC<TubeSheetGraphKeyItemProps> = props => {
  return (
    <div className="ion-flex ion-flex-direction-row ion-align-items-center">
      <div className={classNames('tube-sheet-color-key', props.className)} />
      <div>
        <p>
          <DqText variant="body-strong">{props.title}</DqText>
        </p>
        {props.subtitle && (
          <p>
            <DqText>{props.subtitle}</DqText>
          </p>
        )}
      </div>
    </div>
  );
};

const TubeSheetGraphKeyMain: React.FC = () => {
  const { formatMessage } = useIntl();
  const isDesktopOrTablet = useBreakpoint('sm');
  const keySize = isDesktopOrTablet ? 'auto' : '4';

  return (
    <div className="tube-card-footer">
      <IonRow>
        <IonCol size={keySize}>
          <TubeSheetGraphKeyItem className="symbol-cleaned" title={formatMessage(JobMessages.statusCleaned)} />
        </IonCol>
        <IonCol size={keySize}>
          <TubeSheetGraphKeyItem className="symbol-autostroke" title={formatMessage(JobMessages.statusAutostroke)} />
        </IonCol>
        <IonCol size={keySize}>
          <TubeSheetGraphKeyItem className="symbol-not-cleaned" title={formatMessage(JobMessages.statusNotCleaned)} />
        </IonCol>
        <IonCol size={keySize}>
          <TubeSheetGraphKeyItem className="key-blocked" title={formatMessage(JobMessages.statusBlocked)} />
        </IonCol>
        <IonCol size={keySize}>
          <TubeSheetGraphKeyItem className="symbol-skipped" title={formatMessage(JobMessages.statusSkipped)} />
        </IonCol>
        <IonCol size={keySize}>
          <TubeSheetGraphKeyItem className="symbol-none" title={formatMessage(JobMessages.statusNone)} />
        </IonCol>
      </IonRow>
    </div>
  );
};

const TubeSheetGraphKeyHeat: React.FC<TubeSheetGraphKeyInnerProps> = props => {
  const { formatMessage, formatNumber } = useIntl();
  const legendLabels = useMemo<string[]>(() => {
    if (props.showTimeDetails && props.breakpoints) {
      return [
        `< ${msToTime(props.breakpoints.avg, formatNumber)}`,
        msToTime(props.breakpoints.avg, formatNumber),
        msToTime(props.breakpoints.warning, formatNumber),
        msToTime(props.breakpoints.danger, formatNumber),
      ];
    }
    return [
      formatMessage(JobMessages.halfAverage),
      formatMessage(JobMessages.average),
      formatMessage(JobMessages.twiceAverage),
      formatMessage(JobMessages.tripleAverage),
    ];
  }, [props.showTimeDetails, props.breakpoints, formatMessage, formatNumber]);

  return (
    <div className="tube-card-footer">
      <TubeSheetGraphKeyItem title={formatMessage(JobMessages.tubeSheetTimeInTube)} subtitle={legendLabels[0]} />
      <TubeSheetGraphKeyItem
        className="tube-sheet-heat-breakpoint-avg"
        title={formatMessage(JobMessages.tubeSheetTimeInTube)}
        subtitle={legendLabels[1]}
      />
      <TubeSheetGraphKeyItem
        className="tube-sheet-heat-breakpoint-warning"
        title={formatMessage(JobMessages.tubeSheetTimeInTube)}
        subtitle={legendLabels[2]}
      />
      <TubeSheetGraphKeyItem
        className="tube-sheet-heat-breakpoint-danger"
        title={formatMessage(JobMessages.tubeSheetTimeInTube)}
        subtitle={legendLabels[3]}
      />
    </div>
  );
};

const TubeSheetGraphKeyAutoStroke: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <div className="tube-card-footer">
      <TubeSheetGraphKeyItem title={formatMessage(JobMessages.autoStrokesDidNotOccurredLabel)} />
      <TubeSheetGraphKeyItem
        className="tube-sheet-circle-autostroke"
        title={formatMessage(JobMessages.autoStrokesOccurredLabel)}
      />
    </div>
  );
};

const TubeSheetGraphKeyLance: React.FC<TubeSheetGraphKeyInnerProps> = props => {
  const { formatMessage, formatNumber } = useIntl();
  const legendLabels = useMemo(() => {
    if (props.showTimeDetails && props.breakpoints) {
      const opts: FormatNumberOptions = {
        unit: props.isMetric ? 'meter-per-second' : 'foot-per-second',
        style: 'unit',
        unitDisplay: 'short',
        maximumFractionDigits: 3,
      };
      return [
        `< ${formatNumber(props.breakpoints.avg, opts)}`,
        formatNumber(props.breakpoints.avg, opts),
        formatNumber(props.breakpoints.warning, opts),
        formatNumber(props.breakpoints.danger, opts),
      ];
    }
    return [
      formatMessage(JobMessages.halfAverage),
      formatMessage(JobMessages.average),
      formatMessage(JobMessages.oneAndAHalfAverage),
      formatMessage(JobMessages.twiceAverage),
    ];
  }, [formatMessage, formatNumber, props.breakpoints, props.isMetric, props.showTimeDetails]);

  return (
    <div className="tube-card-footer">
      <TubeSheetGraphKeyItem title={formatMessage(JobMessages.tubeSheetLanceSpeed)} subtitle={legendLabels[0]} />
      <TubeSheetGraphKeyItem
        className="tube-sheet-lance-breakpoint-avg"
        title={formatMessage(JobMessages.tubeSheetLanceSpeed)}
        subtitle={legendLabels[1]}
      />
      <TubeSheetGraphKeyItem
        className="tube-sheet-lance-breakpoint-warning"
        title={formatMessage(JobMessages.tubeSheetLanceSpeed)}
        subtitle={legendLabels[2]}
      />
      <TubeSheetGraphKeyItem
        className="tube-sheet-lance-breakpoint-danger"
        title={formatMessage(JobMessages.tubeSheetLanceSpeed)}
        subtitle={legendLabels[3]}
      />
    </div>
  );
};

const TubeSheetGraphKeyDepth: React.FC<TubeSheetGraphKeyInnerProps> = props => {
  const { formatMessage, formatNumber } = useIntl();
  const legendLabels = useMemo(() => {
    if (props.showTimeDetails && props.breakpoints) {
      const opts: FormatNumberOptions = {
        unit: props.isMetric ? 'meter' : 'foot',
        style: 'unit',
        unitDisplay: 'short',
        maximumFractionDigits: 3,
      };
      return [
        `< ${formatNumber(props.breakpoints.avg, opts)}`,
        formatNumber(props.breakpoints.avg, opts),
        formatNumber(props.breakpoints.warning, opts),
        formatNumber(props.breakpoints.danger, opts),
      ];
    }
    return [
      formatMessage(JobMessages.halfAverage),
      formatMessage(JobMessages.average),
      formatMessage(JobMessages.oneAndAHalfAverage),
      formatMessage(JobMessages.twiceAverage),
    ];
  }, [formatMessage, formatNumber, props.breakpoints, props.isMetric, props.showTimeDetails]);

  return (
    <div className="tube-card-footer">
      <TubeSheetGraphKeyItem title={formatMessage(JobMessages.tubeSheetDepthTitle)} subtitle={legendLabels[0]} />
      <TubeSheetGraphKeyItem
        className="tube-sheet-depth-breakpoint-avg"
        title={formatMessage(JobMessages.tubeSheetDepthTitle)}
        subtitle={legendLabels[1]}
      />
      <TubeSheetGraphKeyItem
        className="tube-sheet-depth-breakpoint-warning"
        title={formatMessage(JobMessages.tubeSheetDepthTitle)}
        subtitle={legendLabels[2]}
      />
      <TubeSheetGraphKeyItem
        className="tube-sheet-depth-breakpoint-danger"
        title={formatMessage(JobMessages.tubeSheetDepthTitle)}
        subtitle={legendLabels[3]}
      />
    </div>
  );
};

interface TubeSheetGraphKeyProps {
  chartType: TubeSheetChartType;
  breakpoints?: TubeSheetGraphData['breakpoints'];
  showTimeDetails: boolean;
  isMetric: boolean;
}

export const TubeSheetGraphKey: React.FC<TubeSheetGraphKeyProps> = props => {
  switch (props.chartType) {
    case TubeSheetChartType.MAIN:
      return <TubeSheetGraphKeyMain />;
    case TubeSheetChartType.HEAT:
      return (
        <TubeSheetGraphKeyHeat
          showTimeDetails={props.showTimeDetails}
          breakpoints={props.breakpoints?.duration}
          isMetric={props.isMetric}
        />
      );
    case TubeSheetChartType.AUTOSTROKES:
      return <TubeSheetGraphKeyAutoStroke />;
    case TubeSheetChartType.LANCESPEED:
      return (
        <TubeSheetGraphKeyLance
          showTimeDetails={props.showTimeDetails}
          breakpoints={props.breakpoints?.lanceSpeed}
          isMetric={props.isMetric}
        />
      );
    case TubeSheetChartType.DEPTH:
      return (
        <TubeSheetGraphKeyDepth
          showTimeDetails={props.showTimeDetails}
          breakpoints={props.breakpoints?.depth}
          isMetric={props.isMetric}
        />
      );
    default:
      assertNever(props.chartType);
  }
};
